import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import { jsPDF } from 'jspdf';
import SafeIcon from '../../common/SafeIcon';
import { formConfig } from '../../config/formConfig';

const FormRenderer = ({ form, isPublicMode = false, route }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({});
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [errorDetails, setErrorDetails] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);

  const [showSamplePanel, setShowSamplePanel] = useState(false);
  const [sampleEmail, setSampleEmail] = useState("");

  const currentSection = form.sections[currentSectionIndex];
  const searchParams = new URLSearchParams(location.search);
  const isDemoMode = searchParams.get('demo') === '1';
  const providedKey = searchParams.get(formConfig.access.publicShare.queryParam);
  const isKeyValid = providedKey === route.shareKey;
  const canShowDemo = !isPublicMode || (isPublicMode && isDemoMode && isKeyValid);

  // --- HELPERS ---
  const randomFrom = (arr) => arr[Math.floor(Math.random() * arr.length)];
  const randomPhoneAU = () => `04${Math.floor(1000 + Math.random() * 9000)} ${Math.floor(100 + Math.random() * 900)} ${Math.floor(100 + Math.random() * 900)}`;
  const randomBusinessName = () => randomFrom(["Acme Solutions", "Lumina Digital", "Apex Legal", "Green Valley Clinic", "Swift Logistics", "Horizon Property", "Blue Wave Marketing"]);
  const randomPersonName = () => randomFrom(["John Smith", "Sarah Jenkins", "Michael Chen", "Emma Wilson", "David Thompson", "Chloe Anderson", "James Richardson"]);
  const randomUrl = () => `https://www.${randomFrom(["example", "business", "portal", "site"])}.com.au`;
  const randomSentence = () => randomFrom([
    "Expanding our digital presence is a key initiative.",
    "The current interface lacks modern polish.",
    "We need to streamline our lead capture process.",
    "Improving trust through clean design is critical.",
    "Our future growth depends on professional branding."
  ]);
  const randomParagraph = () => Array(3).fill(0).map(randomSentence).join(" ");
  const randomFutureDate = (min, max) => {
    const d = new Date();
    d.setDate(d.getDate() + Math.floor(Math.random() * (max - min) + min));
    return d.toISOString().split('T')[0];
  };

  const buildRandomValues = (form, confirmedEmail) => {
    const values = {};
    form.fields.forEach(f => {
      if (f.id === "clientEmail" || f.type === "email") {
        values[f.id] = confirmedEmail;
        return;
      }
      if (f.required && (f.type === "checkbox" || f.type === "toggle")) {
        values[f.id] = true;
        return;
      }
      switch (f.type) {
        case "text": values[f.id] = f.id.toLowerCase().includes("business") ? randomBusinessName() : (f.id.toLowerCase().includes("name") ? randomPersonName() : randomSentence()); break;
        case "tel": values[f.id] = randomPhoneAU(); break;
        case "url": values[f.id] = randomUrl(); break;
        case "textarea": values[f.id] = randomParagraph(); break;
        case "number": values[f.id] = Math.floor(Math.random() * 5000) + 500; break;
        case "date": values[f.id] = randomFutureDate(30, 90); break;
        case "select":
        case "radio": values[f.id] = f.options?.[Math.floor(Math.random() * f.options.length)]?.value || ""; break;
        case "multiselect": values[f.id] = f.options ? f.options.slice(0, 2).map(o => o.value) : []; break;
        case "checkbox":
        case "toggle": values[f.id] = Math.random() > 0.5; break;
        default: values[f.id] = null;
      }
    });
    return values;
  };

  const buildQAPairs = (form, values) => {
    const qaPairs = [];
    form.sections.forEach(section => {
      const sectionQA = { sectionTitle: section.title, pairs: [] };
      section.fieldIds.forEach(fieldId => {
        const field = form.fields.find(f => f.id === fieldId);
        if (!field || field.type === 'hidden') return;
        const val = values[fieldId];
        let displayValue = "Not provided";

        if (val !== undefined && val !== null && val !== "") {
          if (field.type === "multiselect") {
            displayValue = Array.isArray(val) ? val.join(", ") : val;
          } else if (field.type === "checkbox" || field.type === "toggle") {
            displayValue = val ? "Yes" : "No";
          } else {
            displayValue = val.toString();
          }
        }
        sectionQA.pairs.push({ question: field.label, answer: displayValue });
      });
      if (sectionQA.pairs.length > 0) qaPairs.push(sectionQA);
    });
    return qaPairs;
  };

  const generatePDF = (formName, qaPairs, submissionId, submittedAt) => {
    const doc = new jsPDF();
    let y = 20;
    doc.setFontSize(18);
    doc.text(`BPA Solutions - ${formName}`, 20, y);
    y += 15;
    doc.setFontSize(10);
    doc.text(`ID: ${submissionId} | Date: ${submittedAt}`, 20, y);
    y += 15;

    qaPairs.forEach(section => {
      if (y > 270) { doc.addPage(); y = 20; }
      doc.setFontSize(12);
      doc.text(section.sectionTitle, 20, y);
      y += 8;
      section.pairs.forEach(pair => {
        if (y > 270) { doc.addPage(); y = 20; }
        doc.setFontSize(9);
        const lines = doc.splitTextToSize(`${pair.question}: ${pair.answer}`, 170);
        doc.text(lines, 25, y);
        y += (lines.length * 5) + 2;
      });
      y += 5;
    });
    return doc.output('base64');
  };

  const sendEmailRequest = async (payload) => {
    const response = await fetch('/api/send-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Mail delivery failed.");
    }
  };

  const handleFinalSubmit = async (e, customValues = null) => {
    if (e) e.preventDefault();
    const values = customValues || formData;
    setIsSubmitting(true);
    setErrorDetails("");

    const submissionId = `BPA-${Date.now()}`;
    const submittedAt = new Date().toLocaleString();
    const qaPairs = buildQAPairs(form, values);
    const pdfBase64 = generatePDF(form.name, qaPairs, submissionId, submittedAt);

    let qaText = "\nSUBMISSION SUMMARY\n";
    let qaHtml = "<h3>Submission Summary</h3>";
    qaPairs.forEach(s => {
      qaText += `\n[ ${s.sectionTitle} ]\n`;
      qaHtml += `<h4>${s.sectionTitle}</h4><ul>`;
      s.pairs.forEach(p => {
        qaText += `${p.question}: ${p.answer}\n`;
        qaHtml += `<li><strong>${p.question}:</strong> ${p.answer}</li>`;
      });
      qaHtml += "</ul>";
    });

    const replacePlaceholders = (text, isHtml = false) => {
      let result = text
        .replace(/{{form\.name}}/g, form.name)
        .replace(/{{submissionId}}/g, submissionId)
        .replace(/{{submittedAt}}/g, submittedAt);

      Object.keys(values).forEach(key => {
        result = result.replace(new RegExp(`{{submission\\.${key}}}`, 'g'), values[key] || '');
      });
      return result + (isHtml ? qaHtml : qaText);
    };

    const routeConfig = formConfig.email.routes.find(r => r.formId === form.id);

    try {
      // 1. Client Confirmation
      if (routeConfig?.templates.clientConfirmation.enabled) {
        const clientEmail = values.clientEmail || values.adsClientEmail || values.requestorEmail;
        await sendEmailRequest({
          to: [clientEmail],
          subject: replacePlaceholders(routeConfig.templates.clientConfirmation.subject),
          text: replacePlaceholders(routeConfig.templates.clientConfirmation.bodyText),
          html: replacePlaceholders(routeConfig.templates.clientConfirmation.bodyHtml, true),
          attachments: [{
            filename: `${form.id}-copy.pdf`,
            content_type: "application/pdf",
            content: pdfBase64
          }]
        });
      }

      // 2. Wait 4 seconds
      await new Promise(resolve => setTimeout(resolve, 4000));

      // 3. Internal Notification
      if (routeConfig?.templates.internalNotification.enabled) {
        await sendEmailRequest({
          to: ["support@bpasolutions.au"],
          subject: replacePlaceholders(routeConfig.templates.internalNotification.subject),
          text: replacePlaceholders(routeConfig.templates.internalNotification.bodyText),
          html: replacePlaceholders(routeConfig.templates.internalNotification.bodyHtml, true),
          attachments: [{
            filename: `${form.id}-internal.pdf`,
            content_type: "application/pdf",
            content: pdfBase64
          }]
        });
      }

      setSubmissionStatus('success');
    } catch (err) {
      console.error("Submission Error:", err);
      setErrorDetails(err.message);
      setSubmissionStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendSample = (e) => {
    e.preventDefault();
    if (!sampleEmail || !sampleEmail.includes("@")) return alert("Please enter a valid email.");
    const randomData = buildRandomValues(form, sampleEmail);
    setFormData(randomData);
    handleFinalSubmit(null, randomData);
  };

  const handleCopyLink = () => {
    const url = window.location.origin + route.sharePath + "?k=" + route.shareKey;
    navigator.clipboard.writeText(url);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  if (submissionStatus === 'success') {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="max-w-2xl mx-auto text-center py-20 bg-[var(--surface)] rounded-3xl border border-[var(--border)] shadow-2xl">
        <div className="w-20 h-20 bg-[var(--success)]/10 text-[var(--success)] rounded-full flex items-center justify-center mx-auto mb-6">
          <SafeIcon name="CheckCircle" className="text-4xl" />
        </div>
        <h2 className="text-3xl font-black text-[var(--text)] mb-4">Submission Successful!</h2>
        <p className="text-[var(--textMuted)] px-10 mb-8">{form.submission.successMessage}</p>
        {!isPublicMode && <button onClick={() => navigate('/')} className="px-8 py-3 bg-[var(--primary)] text-white font-bold rounded-xl">Return to Hub</button>}
      </motion.div>
    );
  }

  if (submissionStatus === 'error') {
    return (
      <div className="max-w-xl mx-auto text-center py-20 bg-[var(--surface)] rounded-3xl border border-[var(--danger)]/30">
        <div className="w-16 h-16 bg-[var(--danger)]/10 text-[var(--danger)] rounded-full flex items-center justify-center mx-auto mb-6">
          <SafeIcon name="AlertTriangle" className="text-3xl" />
        </div>
        <h2 className="text-2xl font-black text-[var(--text)] mb-2">Submission Failed</h2>
        <p className="text-[var(--textMuted)] mb-2">{form.submission.failureMessage}</p>
        <p className="text-xs text-[var(--danger)] font-mono mb-8 p-4 bg-[var(--background)] rounded-xl mx-8">{errorDetails}</p>
        <button onClick={() => setSubmissionStatus(null)} className="px-8 py-3 bg-[var(--primary)] text-white font-bold rounded-xl">Try Again</button>
      </div>
    );
  }

  const renderField = (fieldId) => {
    const field = form.fields.find(f => f.id === fieldId);
    if (!field) return null;
    const commonClass = "w-full bg-[var(--background)] border border-[var(--border)] rounded-[var(--input-borderRadius)] px-4 py-3 text-[var(--text)] focus:ring-2 focus:ring-[var(--primary)] outline-none";

    return (
      <div key={fieldId} className="flex flex-col gap-2">
        <label className="text-sm font-bold text-[var(--text)]">{field.label} {field.required && "*"}</label>
        {field.type === 'textarea' ? (
          <textarea className={`${commonClass} min-h-[120px]`} value={formData[fieldId] || ''} onChange={(e) => setFormData({...formData, [fieldId]: e.target.value})} required={field.required} />
        ) : field.type === 'select' ? (
          <select className={commonClass} value={formData[fieldId] || ''} onChange={(e) => setFormData({...formData, [fieldId]: e.target.value})} required={field.required}>
            <option value="">Select...</option>
            {field.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ) : field.type === 'checkbox' ? (
          <label className="flex items-center gap-3 p-4 bg-[var(--surfaceAlt)] rounded-xl cursor-pointer">
            <input type="checkbox" className="w-5 h-5" checked={formData[fieldId] || false} onChange={(e) => setFormData({...formData, [fieldId]: e.target.checked})} />
            <span className="text-sm font-medium">{field.label}</span>
          </label>
        ) : (
          <input type={field.type} className={commonClass} value={formData[fieldId] || ''} onChange={(e) => setFormData({...formData, [fieldId]: e.target.value})} required={field.required} />
        )}
      </div>
    );
  };

  return (
    <div className="max-w-3xl mx-auto pb-20">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <SafeIcon name={form.icon} className="text-3xl text-[var(--primary)]" />
          <h2 className="text-4xl font-black text-[var(--text)]">{form.name}</h2>
        </div>
        <div className="flex gap-2">
          {canShowDemo && (
            <button onClick={() => setShowSamplePanel(true)} className="px-4 py-2 rounded-xl bg-[var(--warning)]/10 text-[var(--warning)] text-xs font-bold border border-[var(--warning)]/20">
              Send sample completed form
            </button>
          )}
          {!isPublicMode && <button onClick={handleCopyLink} className="px-4 py-2 rounded-xl bg-[var(--surfaceAlt)] text-xs font-bold border border-[var(--border)]">{copySuccess ? "Copied!" : "Copy Link"}</button>}
        </div>
      </div>

      <AnimatePresence>
        {showSamplePanel && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="mb-8 overflow-hidden">
            <div className="bg-[var(--warning)]/5 border border-[var(--warning)]/20 p-8 rounded-3xl">
              <h4 className="font-black mb-2">Confirm Sample Recipient</h4>
              <p className="text-xs text-[var(--textMuted)] mb-4">A realistic sample will be generated and sent to this email.</p>
              <div className="flex gap-3">
                <input type="email" placeholder="Recipient email" className="flex-1 px-4 py-3 rounded-xl border border-[var(--border)] outline-none" value={sampleEmail} onChange={(e) => setSampleEmail(e.target.value)} />
                <button onClick={handleSendSample} className="px-6 py-3 bg-[var(--warning)] text-white font-black rounded-xl text-sm">Send Sample</button>
                <button onClick={() => setShowSamplePanel(false)} className="px-4 py-3 text-[var(--textMuted)] text-sm font-bold">Cancel</button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-3xl p-10 shadow-2xl">
        <h3 className="text-xl font-bold mb-8">{currentSection.title}</h3>
        <div className="space-y-6">
          {currentSection.fieldIds.map(renderField)}
        </div>
        <div className="mt-12 flex justify-between border-t border-[var(--border)] pt-8">
          <button disabled={currentSectionIndex === 0} onClick={() => setCurrentSectionIndex(prev => prev - 1)} className="px-6 py-3 font-bold text-[var(--textMuted)] disabled:opacity-20">Back</button>
          {currentSectionIndex < form.sections.length - 1 ? (
            <button onClick={() => setCurrentSectionIndex(prev => prev + 1)} className="px-8 py-3 bg-[var(--primary)] text-white font-bold rounded-xl">Next Step</button>
          ) : (
            <button disabled={isSubmitting} onClick={handleFinalSubmit} className="px-10 py-4 bg-[var(--success)] text-white font-black rounded-xl shadow-lg">
              {isSubmitting ? "Sending..." : "Submit Intake"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormRenderer;